import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Phone, X } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS, PHONE_DISPLAY, PHONE_HREF } from "./data";
import { scrollToId, EASE } from "./motion";

export const CallButton = ({ testId, className = "" }) => (
  <a
    href={PHONE_HREF}
    data-testid={testId}
    className={`group inline-flex items-center gap-2.5 rounded-full bg-brand-400 px-5 py-2.5 font-display text-sm font-bold text-[#0A0A0A] transition-[background-color,transform] duration-300 hover:scale-[1.03] hover:bg-brand-300 ${className}`}
  >
    <span className="relative flex h-2 w-2">
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#0A0A0A]/60" />
      <span className="relative inline-flex h-2 w-2 rounded-full bg-[#0A0A0A]" />
    </span>
    <Phone className="h-4 w-4" strokeWidth={2.5} />
    {PHONE_DISPLAY}
  </a>
);

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed inset-x-0 top-0 z-50 border-b-2 border-black heather transition-shadow duration-500 ${scrolled ? "shadow-[0_10px_30px_rgba(0,0,0,0.45)]" : ""}`}
    >
      <div className="mx-auto flex max-w-[1440px] items-center justify-between gap-4 px-5 py-3 lg:px-12">
        <Logo onClick={() => go("top")} />
        <nav className="hidden items-center gap-6 lg:flex xl:gap-8">
          {NAV_LINKS.map((l) => (
            <button key={l.id} onClick={() => go(l.id)} data-testid={`nav-link-${l.id}`} className="nav-link text-sm font-semibold text-zinc-100 hover:text-white">
              {l.label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <CallButton testId="nav-call-button" className="hidden sm:inline-flex" />
          <button onClick={() => setOpen((v) => !v)} data-testid="nav-mobile-toggle" aria-label="Menu" className="grid h-11 w-11 shrink-0 place-items-center rounded-full border-2 border-zinc-300 text-white lg:hidden">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.4, ease: EASE }}
            data-testid="nav-mobile-menu"
            className="border-t border-white/15 px-5 pb-8 pt-4 lg:hidden"
          >
            {NAV_LINKS.map((l) => (
              <button key={l.id} onClick={() => go(l.id)} data-testid={`mobile-nav-link-${l.id}`} className="block w-full border-b border-white/15 py-4 text-left font-display text-2xl font-bold text-white">
                {l.label}
              </button>
            ))}
            <CallButton testId="mobile-menu-call-button" className="mt-6 w-full justify-center py-4 text-base" />
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
